import React, { useState } from "react";

import { classNames, shiftTag, niceNumber, fromShiftTag, getDay, isWeekend } from "@/lib/util";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import { Dropdown } from "react-bootstrap";
import { SHIFT_MODIFIERS } from "@/lib/models";
import { LINE_TAGS } from "@/lib/ManufacturingTags.generated";
import { mapShiftsToLines, mapShiftsToTime, formatDate } from "./Preplanning";
import PreplanningShiftCompetencesModal from "../modals/PreplanningShiftCompetencesModal";
import PreplanningOverrideShiftModal from "../modals/PreplanningOverrideShiftModal";
import PreplanningEditLineParametersModal, { getLineCapacity } from "../modals/PreplanningEditLineParametersModal";
import PreplanningTableShiftCell from "./PreplanningTableShiftCell";
import * as Auth from "@/lib/auth";

import type { ProductionUntilTodayOverall } from "@/lib/models";
import type {
    ILineData,
    IPreplanningDataLine,
    IShiftLineRec,
    IPreplanningOverrideDataLine,
    IHoliday,
    IPreplanningDataExpectedProduction,
    IShiftPersonLineRecPerson,
} from "@/lib/backend/manufacturing2.generated.types";

import "./PreplanningTable.scss";

export type AvailablePeopleMap = Map<string, IShiftPersonLineRecPerson[]>;

export type CompetenceTitleFactorMap = Map<string, number>;

export type ShiftCompetencesMap = Map<string, CompetenceTitleFactorMap>;

export type CompetenceSumUp = {
    all_needed_competences_by_shift: ShiftCompetencesMap;
    all_needed_competences_by_day: ShiftCompetencesMap;
};

export type ShiftSummarized = {
    shift_tag: string;
    line_external_ids: string[];
};

export type CompetenceSumUpInput = {
    shifts: ShiftSummarized[];
};

type OverrideModalData = {
    shift: IPreplanningOverrideDataLine;
    line_external_id: string;
};

const getShiftName = (shift: number): string => {
    const shift_names = [
        translate("common.morning_shift", "Early"),
        translate("common.afternoon_shift", "Late"),
        translate("common.night_shift", "Night"),
    ];
    return shift_names[shift] || "";
};

/**
 * Get the short title of a competence from its full title.
 * @param {string} competence_title - The full title of the competence.
 * @returns {string} - The short title of the competence.
 */
const getCompetenceShortTitle = (competence_title: string): string => {
    const match = competence_title.match(/\(([^)]+)\)/);
    return match !== null ? match[1] : competence_title;
};

/**
 * Format the tooltip content for competences.
 * @param {CompetenceTitleFactorMap} competence_title_to_factor_map - A map of competence title to their factors.
 * @returns {string} - The formatted tooltip content.
 */
const formatCompetenceTooltipContent = (competence_title_to_factor_map: CompetenceTitleFactorMap): string => {
    let tooltip_content = "";
    for (const [name, factor] of competence_title_to_factor_map.entries()) {
        if (name && factor) {
            const short_name = getCompetenceShortTitle(name);
            tooltip_content += `${short_name}: ${factor}\n`;
        }
    }

    return tooltip_content;
};

/**
 * Calculate the balance of competences (needed versus available).
 * @param {number} available - The number of available competences.
 * @param {number} needed - The number of needed competences.
 * @param {number} tolerance - The tolerance level for considering the balance as sufficient.
 * @returns {number} - The balance of competences.
 */
const calculateCompetenceBalance = (available: number, needed: number, tolerance: number = 3): number => {
    let balance = needed - available;
    if (Math.abs(balance) < tolerance) {
        balance = 0;
    }

    return balance;
};

const PreplanningTableCompetencesCell = (props: {
    available_competences: number;
    needed_competences: number;
    disabled?: boolean;
    onButtonClick: () => void;
}) => {
    const { available_competences, needed_competences, disabled, onButtonClick } = props;

    const balance = calculateCompetenceBalance(available_competences, needed_competences);

    return (
        <div
            className={classNames("preplanning__table-competences", {
                "is-insufficient": balance > 0,
                "is-sufficient": balance === 0,
                "is-excess": balance < 0,
            })}
        >
            <button
                className={classNames("preplanning__competence-details-button btn btn-short btn-flat-light")}
                onClick={onButtonClick}
                disabled={disabled}
            >
                <i
                    className={classNames("fas", {
                        "fa-eye": needed_competences > 0,
                        "fa-eye-slash": needed_competences === 0,
                    })}
                />
                <span>{available_competences}</span>
            </button>
        </div>
    );
};

export default function PreplanningTable(props: {
    line_group_uuid: string;
    lines: ILineData[];
    child_lines: ILineData[];
    preplanning_lines: IPreplanningDataLine[];
    shifts: IShiftLineRec[];
    shiftChanged: (shift: IShiftLineRec) => boolean;
    onShiftToggle: (shift: IShiftLineRec) => void;
    openEditLineModal: (line_uuid: string) => void;
    getCompetences: (line_external_ids: string[]) => CompetenceTitleFactorMap;
    getCompetencesSumUp: (input_data: CompetenceSumUpInput, short_name?: boolean) => CompetenceSumUp;
    lineGroupMap: Map<string, string>;
    updateShiftData: (shift: IPreplanningOverrideDataLine, line_external_id: string) => void;
    map_available_people_per_shift: AvailablePeopleMap;
    map_available_people_per_day: AvailablePeopleMap;
    productGroupWithLines: Map<string, string[]>;
    sum_per_line: Map<string, ProductionUntilTodayOverall>;
    product_group_sum_map: Map<string, ProductionUntilTodayOverall>;
    production_status_period: boolean;
    public_holidays: IHoliday[];
    expected_productions: IPreplanningDataExpectedProduction[];
    onLineParametersChange: (line: ILineData) => void;
}) {
    const [competences_modal_show, setCompetencesModalShow] = useState(false);
    const [override_modal_show, setOverrideModalShow] = useState(false);
    const [override_modal_data, setOverrideModalData] = useState<OverrideModalData>({
        shift: {
            shift_tag: "",
            quantity: 0,
            comment: "",
            type: SHIFT_MODIFIERS.none,
        },
        line_external_id: "",
    });
    const [has_override, setHasOverride] = useState(false);
    const [show_edit_line_parameters_modal, setShowEditLineParametersModal] = useState(false);
    const [selected_line, setSelectedLine] = useState<ILineData | null>(null);
    const [selected_time_period_tag, setSelectedTimePeriodTag] = useState<string>("");

    const {
        child_lines,
        line_group_uuid,
        lines,
        shifts,
        shiftChanged,
        production_status_period,
        onLineParametersChange,
    } = props;

    const handleEditLineParametersModalOpen = (line: ILineData) => {
        setSelectedLine(line);
        setShowEditLineParametersModal(true);
    };

    const handleEditLineParametersModalClose = () => {
        setShowEditLineParametersModal(false);
        setSelectedLine(null);
    };

    const handleLineParametersSave = (line: ILineData) => {
        setShowEditLineParametersModal(false);
        setSelectedLine(null);
        onLineParametersChange(line);
    };

    const line_shifts_map = mapShiftsToLines(shifts);
    const first_line_shifts = line_shifts_map.values().next().value;
    if (first_line_shifts === undefined) {
        return null;
    }

    // Initialize a map to hold the relationship between shift tags and line external ids
    const map_of_shift_tags_to_line_external_ids: Map<string, string[]> = new Map();

    // Iterate over the line_shifts_map
    for (const [line_uuid, shifts] of line_shifts_map) {
        // Find the line with the matching uuid and get its external id
        let line_external_id;
        const line = lines.find(line => line.uuid === line_uuid);
        if (line) {
            line_external_id = line.external_id;
        }

        if (line_external_id) {
            for (const shift of shifts) {
                if (shift.enabled) {
                    const shift_tag = shiftTag(shift.year, shift.week, shift.shift_number);

                    // Get the existing line external ids for this shift tag, or an empty array if none exist
                    const line_external_ids_for_shift_tag = map_of_shift_tags_to_line_external_ids.get(shift_tag) || [];

                    line_external_ids_for_shift_tag.push(line_external_id);

                    map_of_shift_tags_to_line_external_ids.set(shift_tag, line_external_ids_for_shift_tag);
                }
            }
        }
    }

    // Convert the map of shift tags to line external ids into an array of objects
    // Create an array from the map
    const shifts_summarized = Array.from(
        // The map to convert
        map_of_shift_tags_to_line_external_ids,

        // Map function to convert each entry in the map
        ([shift_tag, line_external_ids]) => ({
            shift_tag,
            line_external_ids,
        })
    );
    const competences = props.getCompetencesSumUp({ shifts: shifts_summarized }, false);
    const competences_sum_up_by_shift = competences.all_needed_competences_by_shift;

    const sum_production_per_day: { shift_day: number; sum: number }[] = [];
    const sum_planned_per_day: { shift_day: number; sum: number }[] = [];

    const first_time_shifts_map = mapShiftsToTime(first_line_shifts);

    const handleHideOverrideShiftModal = () => {
        setOverrideModalShow(false);
        setOverrideModalData({
            shift: {
                shift_tag: "",
                quantity: 0,
                comment: "",
                type: SHIFT_MODIFIERS.none,
            },
            line_external_id: "",
        });
    };

    const handleUpdateShiftData = (shift: IPreplanningOverrideDataLine) => {
        props.updateShiftData(shift, override_modal_data.line_external_id);
        handleHideOverrideShiftModal();
    };

    const handleOnShiftClick = (
        event: React.MouseEvent<HTMLDivElement>,
        shift_tag: string,
        shift: IShiftLineRec,
        line_external_id: string,
        quantity: number,
        override_shift?: IPreplanningOverrideDataLine
    ) => {
        event.stopPropagation();

        const shift_date = formatDate(new Date(shift.shift_date));
        const current_date = formatDate(new Date());
        const can_disable_shift = shift_date >= current_date;

        // Meta is the Command key on Mac
        const is_modifier_pressed: boolean = event.getModifierState("Control") || event.getModifierState("Meta");
        if (is_modifier_pressed) {
            const override_shift_data = override_shift ?? {
                shift_tag,
                quantity,
                comment: "",
                type: SHIFT_MODIFIERS.none,
            };
            setHasOverride(Boolean(override_shift));
            setOverrideModalData({
                shift: override_shift_data,
                line_external_id: line_external_id,
            });
            setOverrideModalShow(true);
        } else if (can_disable_shift) {
            props.onShiftToggle(shift);
        }
    };

    const handleShiftCompetencesModalClose = () => {
        setCompetencesModalShow(false);
        setSelectedTimePeriodTag("");
    };

    const handleCompetencesButtonClick = (time_period_tag: string) => {
        setSelectedTimePeriodTag(time_period_tag);
        setCompetencesModalShow(true);
    };

    return (
        <>
            <table className="preplanning__table table table-bordered">
                <thead>
                    <tr>
                        <th className="text-left">
                            <div className="preplanning__table-cell text-left">{translate("common.line", "Line")}</div>
                        </th>
                        {[...first_time_shifts_map.keys()].map(shift_date => {
                            const date = new Date(shift_date);
                            const day = getDay(date);
                            const is_weekend = isWeekend(date);
                            const current_date = new Date();
                            let is_today_classname = "";
                            const is_holiday = !!props.public_holidays.find(
                                holiday => holiday.date === formatDate(date)
                            );

                            if (
                                date.getDate() === current_date.getDate() &&
                                date.getMonth() === current_date.getMonth() &&
                                date.getFullYear() === current_date.getFullYear()
                            ) {
                                is_today_classname = " font-weight-bold";
                            }

                            return (
                                <th
                                    key={shift_date}
                                    className={classNames({
                                        "is-weekend": is_weekend,
                                        "font-weight-bold": is_today_classname,
                                        "bg-holiday": is_holiday,
                                        "text-white": is_holiday,
                                    })}
                                >
                                    <div className="preplanning__table-cell flex-row">
                                        <span
                                            className={classNames("flex-grow-1 pr-2", {
                                                "text-danger": is_weekend,
                                                "text-muted": !is_weekend,
                                            })}
                                        >
                                            {translate(`common.weekday${day}`).slice(0, 3)}
                                        </span>
                                        <strong
                                            className={classNames({
                                                "text-danger": is_weekend,
                                            })}
                                        >
                                            {`${date.getDate()}`.padStart(2, "0")}
                                        </strong>
                                    </div>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {lines.map((line, i) => {
                        const line_shifts = line_shifts_map.get(line.uuid);

                        if (line_shifts === undefined) {
                            return null;
                        }
                        const group = props.lineGroupMap.get(line.external_id.replace("058", "")) || "";
                        const production = props.product_group_sum_map.get(group);

                        let planned_time_period = 0;
                        let planned_over_plan = 0;
                        let status = 0;
                        const material_group = props.expected_productions.find(
                            expected_production => expected_production.material_group === group
                        );
                        let sales_plan_qty = 0;
                        if (material_group !== undefined) {
                            sales_plan_qty = material_group.quantity;
                        }

                        if (
                            production !== undefined &&
                            production.planned !== undefined &&
                            production.produced !== undefined
                        ) {
                            planned_time_period = production_status_period
                                ? production.planned.overall
                                : production.planned.until_today;
                            planned_over_plan = planned_time_period - sales_plan_qty;

                            status = planned_over_plan - sales_plan_qty;
                        }

                        const time_shifts_map = mapShiftsToTime(line_shifts);
                        return (
                            <tr key={i}>
                                {[...time_shifts_map.values()].map((shifts, j) => {
                                    const is_weekend =
                                        shifts.length > 0 ? isWeekend(new Date(shifts[0].shift_date)) : false;
                                    let produced_sum = 0;
                                    let planned_sum = 0;

                                    let is_today_classname = "";
                                    const date = formatDate(new Date(shifts[0].shift_date));
                                    const is_holiday = props.public_holidays.find(holiday => holiday.date === date);

                                    const current_date = formatDate(new Date());
                                    const is_before_today = date < current_date;
                                    let nice_number_planned = "";
                                    let nice_number_expected = "";

                                    nice_number_planned = niceNumber(planned_time_period);
                                    nice_number_expected = niceNumber(sales_plan_qty);
                                    const planned_or_produced = production_status_period
                                        ? translate("Preplanning.planned", "Planned")
                                        : translate("Preplanning.produced", "Produced");

                                    const tooltip_title =
                                        planned_or_produced +
                                        ": " +
                                        nice_number_planned +
                                        " - " +
                                        translate("Preplanning.expected", "Expected") +
                                        ": " +
                                        nice_number_expected;

                                    if (date === current_date) {
                                        is_today_classname = "is-today";
                                    }

                                    return (
                                        <React.Fragment key={j}>
                                            {j === 0 && (
                                                <td>
                                                    <div className="preplanning__table-cell text-left flex-row p-0">
                                                        <div className="preplanning__table-line">
                                                            <div className="preplanning__table-line-header">
                                                                <strong className="text-truncate">{line.title}</strong>
                                                                {Auth.roleCanEditPreplanning() && (
                                                                    <div>
                                                                        <Dropdown>
                                                                            <Dropdown.Toggle
                                                                                as="button"
                                                                                className="btn btn-short btn-flat-light py-0 my-n1"
                                                                            >
                                                                                <i className="fas fa-ellipsis-v align-middle" />
                                                                            </Dropdown.Toggle>
                                                                            <Dropdown.Menu>
                                                                                <Dropdown.Item
                                                                                    as="button"
                                                                                    onClick={() =>
                                                                                        props.openEditLineModal(
                                                                                            line.uuid
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    {translate(
                                                                                        "Calendar_line.select_shift_template",
                                                                                        "Select shift template"
                                                                                    )}
                                                                                </Dropdown.Item>
                                                                                <Dropdown.Item
                                                                                    as="button"
                                                                                    onClick={() =>
                                                                                        handleEditLineParametersModalOpen(
                                                                                            line
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    {translate(
                                                                                        "Preplanning.edit_line_parameters",
                                                                                        "Edit line parameters"
                                                                                    )}
                                                                                </Dropdown.Item>
                                                                            </Dropdown.Menu>
                                                                        </Dropdown>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="preplanning__table-line-details">
                                                                {group && (
                                                                    <strong>
                                                                        <abbr
                                                                            title={translate(
                                                                                "Preplanning.product_group",
                                                                                "Product Group"
                                                                            )}
                                                                            aria-label={translate(
                                                                                "Preplanning.product_group",
                                                                                "Product Group"
                                                                            )}
                                                                        >
                                                                            {translate(
                                                                                "Preplanning.product_group_short",
                                                                                "PG"
                                                                            )}
                                                                        </abbr>
                                                                        : {group}
                                                                    </strong>
                                                                )}
                                                                {group && (
                                                                    <span
                                                                        title={tooltip_title}
                                                                        className={classNames("badge badge-pill", {
                                                                            "badge-success": status > 0,
                                                                            "badge-warning": status === 0,
                                                                            "badge-danger": status < 0,
                                                                        })}
                                                                    >
                                                                        {translate("common.status", "Status")}:{" "}
                                                                        {niceNumber(planned_over_plan, 0)}
                                                                    </span>
                                                                )}
                                                                {line.tags[LINE_TAGS.capacity_factor] !== undefined && (
                                                                    <span className={"badge badge-pill badge-info"}>
                                                                        {translate(
                                                                            "common.line_capacity",
                                                                            "Line capacity"
                                                                        )}
                                                                        : {niceNumber(getLineCapacity(line))}%
                                                                    </span>
                                                                )}
                                                                {line.priority !== null && (
                                                                    <span
                                                                        className={"badge badge-pill badge-secondary"}
                                                                    >
                                                                        {translate("common.priority", "Priority")}:{" "}
                                                                        {line.priority}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="preplanning__table-line-labels p-0">
                                                            {shifts.map((shift, k) => (
                                                                <div key={k}>
                                                                    {k + 1}. {getShiftName(k)}
                                                                </div>
                                                            ))}
                                                            <div>
                                                                <em>{translate("Preplanning.planned", "Planned")}</em>
                                                            </div>
                                                            <div>
                                                                <em>{translate("Preplanning.produced", "Produced")}</em>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            )}
                                            <td className={classNames({ "is-weekend": is_weekend })}>
                                                <div className="preplanning__table-cell shift p-0">
                                                    {shifts.map((shift, k) => {
                                                        const shift_changed = shiftChanged(shift);
                                                        const shift_tag = shiftTag(
                                                            shift.year,
                                                            shift.week,
                                                            shift.shift_number
                                                        );
                                                        const line_external_id = line.external_id;
                                                        let is_planned_classname = "";

                                                        const competence_title_to_factor_map = props.getCompetences([
                                                            line.external_id,
                                                        ]);
                                                        let tooltip_content =
                                                            formatCompetenceTooltipContent(
                                                                competence_title_to_factor_map
                                                            );

                                                        let quantity_produced = 0;
                                                        let quantity_planned = 0;

                                                        const line_data = props.preplanning_lines.find(
                                                            line => line.line_external_id === line_external_id
                                                        );
                                                        if (line_data !== undefined) {
                                                            const materials = line_data.materials;
                                                            let once = true;

                                                            materials.forEach(material => {
                                                                const material_items_planned = material.planned;
                                                                const material_items_produced = material.produced;

                                                                material_items_planned.forEach(item => {
                                                                    if (item.shift_tag === shift_tag) {
                                                                        quantity_planned += item.quantity;
                                                                    }
                                                                });
                                                                material_items_produced.forEach(item => {
                                                                    if (item.shift_tag === shift_tag) {
                                                                        quantity_produced += item.quantity;
                                                                    }
                                                                });

                                                                const overrides = line_data.overrides;
                                                                if (once) {
                                                                    if (
                                                                        overrides !== undefined &&
                                                                        overrides.length > 0
                                                                    ) {
                                                                        overrides.forEach(override => {
                                                                            if (override.shift_tag === shift_tag) {
                                                                                const override_quantity =
                                                                                    override.quantity
                                                                                        ? `\nQuantity: ${override.quantity}`.substring(
                                                                                              0,
                                                                                              50
                                                                                          )
                                                                                        : "";
                                                                                const comment = override.comment
                                                                                    ? `\nComment: ${override.comment}`.substring(
                                                                                          0,
                                                                                          50
                                                                                      )
                                                                                    : "";
                                                                                const type =
                                                                                    override.type !==
                                                                                    SHIFT_MODIFIERS.none
                                                                                        ? `\nType: ${override.type}`.substring(
                                                                                              0,
                                                                                              50
                                                                                          )
                                                                                        : "";
                                                                                tooltip_content +=
                                                                                    override_quantity + comment + type;
                                                                            }
                                                                        });
                                                                    }
                                                                }
                                                                once = false;
                                                            });
                                                        }

                                                        let override_shift;
                                                        const line_overrides = props.preplanning_lines.find(
                                                            l => l.line_external_id === line.external_id
                                                        );
                                                        if (line_overrides !== undefined) {
                                                            if (line_overrides.overrides === undefined) {
                                                                line_overrides.overrides = [];
                                                            }
                                                            override_shift = line_overrides.overrides.find(
                                                                override => override.shift_tag === shift_tag
                                                            );
                                                            if (
                                                                override_shift !== undefined &&
                                                                override_shift.quantity >= 0
                                                            ) {
                                                                quantity_planned = override_shift.quantity;
                                                            }
                                                        }

                                                        produced_sum += quantity_produced;
                                                        planned_sum += quantity_planned;

                                                        const shift_day = fromShiftTag(shift_tag).getDate();
                                                        const find_day_production = sum_production_per_day.find(
                                                            element => element.shift_day === shift_day
                                                        );
                                                        const find_day_planned = sum_planned_per_day.find(
                                                            element => element.shift_day === shift_day
                                                        );

                                                        if (find_day_production !== undefined) {
                                                            find_day_production.sum += quantity_produced;
                                                        } else {
                                                            const sum = quantity_produced;
                                                            sum_production_per_day.push({ shift_day, sum });
                                                        }

                                                        if (find_day_planned !== undefined) {
                                                            find_day_planned.sum += quantity_planned;
                                                        } else {
                                                            const sum = quantity_planned;
                                                            sum_planned_per_day.push({ shift_day, sum });
                                                        }

                                                        const display_quantity = is_before_today
                                                            ? quantity_produced
                                                            : quantity_planned;

                                                        return (
                                                            <div
                                                                key={k}
                                                                className={
                                                                    classNames(
                                                                        "preplanning__table-shift",
                                                                        is_today_classname,
                                                                        {
                                                                            "is-enabled": shift.enabled,
                                                                            "has-changed": shift_changed,
                                                                            "bg-holiday": is_holiday,
                                                                            "text-white": is_holiday,
                                                                        }
                                                                    ) + is_planned_classname
                                                                }
                                                                onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                                                                    Auth.roleCanEditPreplanning() &&
                                                                    handleOnShiftClick(
                                                                        event,
                                                                        shift_tag,
                                                                        shift,
                                                                        line_external_id,
                                                                        display_quantity,
                                                                        override_shift
                                                                    )
                                                                }
                                                                title={tooltip_content}
                                                            >
                                                                <PreplanningTableShiftCell
                                                                    shift={shift}
                                                                    shift_changed={shift_changed}
                                                                    override_shift={override_shift}
                                                                    display_quantity={display_quantity}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                    <div
                                                        className={classNames(
                                                            "preplanning__table-planned",
                                                            is_today_classname
                                                        )}
                                                    >
                                                        {niceNumber(planned_sum)}
                                                    </div>
                                                    <div
                                                        className={classNames(
                                                            "preplanning__table-produced",
                                                            is_today_classname
                                                        )}
                                                    >
                                                        {niceNumber(produced_sum)}
                                                    </div>
                                                </div>
                                            </td>
                                        </React.Fragment>
                                    );
                                })}
                            </tr>
                        );
                    })}
                    <tr>
                        {[...first_time_shifts_map.values()].map((shifts, j) => {
                            const is_weekend = shifts.length > 0 ? isWeekend(new Date(shifts[0].shift_date)) : false;
                            const produced_day = sum_production_per_day.find(sum => sum.shift_day === j + 1);
                            const planned_day = sum_planned_per_day.find(sum => sum.shift_day === j + 1);

                            const quantity_produced = produced_day ? produced_day.sum : 0;
                            const quantity_planned = planned_day ? planned_day.sum : 0;
                            return (
                                <React.Fragment key={j}>
                                    {j === 0 && (
                                        <td>
                                            <div className="preplanning__table-cell text-left flex-row p-0">
                                                <div className="preplanning__table-line">
                                                    <strong>{translate("Preplanning.output_sum", "Output sum")}</strong>
                                                </div>

                                                <div className="preplanning__table-line-labels p-0">
                                                    <div>
                                                        <em>{translate("Preplanning.planned", "Planned")}</em>
                                                    </div>
                                                    <div>
                                                        <em>{translate("Preplanning.produced", "Produced")}</em>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    )}
                                    <td className={classNames({ "is-weekend": is_weekend })}>
                                        <div className="preplanning__table-cell shift p-0">
                                            <div className="preplanning__table-planned">
                                                {quantity_planned >= 0 ? niceNumber(quantity_planned) : ""}
                                            </div>
                                            <div className="preplanning__table-produced">
                                                {quantity_produced >= 0 ? niceNumber(quantity_produced) : ""}
                                            </div>
                                        </div>
                                    </td>
                                </React.Fragment>
                            );
                        })}
                    </tr>
                    <tr>
                        {/* TODO - map of days, each three shifts */}
                        {[...first_time_shifts_map.values()].map((shifts, j) => {
                            const is_weekend = shifts.length > 0 ? isWeekend(new Date(shifts[0].shift_date)) : false;
                            const shift_day = formatDate(new Date(shifts[0].shift_date));

                            let available_competences_per_day = 0;
                            let needed_competences_per_day = 0;

                            return (
                                <React.Fragment key={j}>
                                    {j === 0 && (
                                        <td>
                                            <div className="preplanning__table-cell text-left flex-row p-0">
                                                <div className="preplanning__table-line">
                                                    <strong>
                                                        {translate("Manufacturing.People.competences", "Competences")}
                                                    </strong>
                                                </div>

                                                <div className="preplanning__table-line-labels p-0">
                                                    {shifts.map((shift, k) => (
                                                        <div key={k}>
                                                            {k + 1}. {getShiftName(k)}
                                                        </div>
                                                    ))}
                                                    <div>
                                                        <em>{translate("common.sum", "Sum")}</em>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    )}
                                    <td className={classNames({ "is-weekend": is_weekend })}>
                                        <div className="preplanning__table-cell shift p-0">
                                            {shifts.map((shift, m) => {
                                                const shift_tag = shiftTag(shift.year, shift.week, shift.shift_number);

                                                // Calculate needed competences
                                                const competences_summary = competences_sum_up_by_shift.get(shift_tag);
                                                const needed_competences =
                                                    competences_summary === undefined
                                                        ? 0
                                                        : Array.from(competences_summary.values()).reduce(
                                                              (sum, factor) => sum + factor,
                                                              0
                                                          );

                                                // Calculate available competences
                                                // Note: Available competences are currently approximated by the number
                                                // of available people per shift. This doesn't match available competences
                                                // in the competences dialog, where aproximation is based on the result
                                                // of people scheduling algorithm.
                                                const available_people =
                                                    props.map_available_people_per_shift.get(shift_tag);
                                                const available_competences =
                                                    available_people === undefined ? 0 : available_people.length;

                                                // Update the competences for the day
                                                const competence_balance = calculateCompetenceBalance(
                                                    available_competences,
                                                    needed_competences
                                                );
                                                available_competences_per_day += available_competences;
                                                needed_competences_per_day +=
                                                    competence_balance === 0
                                                        ? // If balance is zero, round to available competences for the shift
                                                          available_competences
                                                        : needed_competences;

                                                return (
                                                    <PreplanningTableCompetencesCell
                                                        key={m}
                                                        available_competences={available_competences}
                                                        needed_competences={needed_competences}
                                                        disabled={needed_competences === 0}
                                                        onButtonClick={() => handleCompetencesButtonClick(shift_tag)}
                                                    />
                                                );
                                            })}
                                            <PreplanningTableCompetencesCell
                                                available_competences={available_competences_per_day}
                                                needed_competences={needed_competences_per_day}
                                                disabled={needed_competences_per_day === 0}
                                                onButtonClick={() => handleCompetencesButtonClick(shift_day)}
                                            />
                                        </div>
                                    </td>
                                </React.Fragment>
                            );
                        })}
                    </tr>
                </tbody>
            </table>
            <PreplanningShiftCompetencesModal
                available_people={
                    props.map_available_people_per_shift.get(selected_time_period_tag) ||
                    props.map_available_people_per_day.get(selected_time_period_tag) ||
                    []
                }
                child_lines={child_lines}
                lines={lines}
                line_group_uuid={line_group_uuid}
                shifts={shifts}
                show={competences_modal_show}
                time_period_tag={selected_time_period_tag}
                onHide={handleShiftCompetencesModalClose}
            />
            <PreplanningOverrideShiftModal
                show={override_modal_show}
                onUpdateShiftData={handleUpdateShiftData}
                onHide={handleHideOverrideShiftModal}
                shift={override_modal_data.shift}
                line_external_id={override_modal_data.line_external_id}
                canSave={props.preplanning_lines.length > 0}
                has_override={has_override}
            />
            <PreplanningEditLineParametersModal
                show={show_edit_line_parameters_modal}
                line={selected_line}
                onSave={handleLineParametersSave}
                onClose={handleEditLineParametersModalClose}
            />
        </>
    );
}
